import { resolveActionGroup } from "./getSchemaForAction";

/**
 * stripScoreFromOverlayTake — only an action that owns Main may write the
 * scoreline.
 *
 * THE FAULT (OPUS-1302)
 * ---------------------
 * Since form-lib 0.9.7 every BAKE is composed on top of the Default caption,
 * and Default is the only caption carrying Main — team identity AND the score.
 * Its copy of the score comes from the form tree, written when the Default was
 * merged in at open and not refreshed since, so an overlay take republishes a
 * snapshot that can be a goal or more behind. Measured on the dev broker: the
 * live score was 2-1 and a Sponsor take sent 1-0, reverting the board.
 *
 * THE RULE
 * --------
 * Derived from the caption group each action owns (resolveActionGroup), never
 * a list of action names kept here:
 *
 *   - a sectional action (Sponsor, FreeText, Cards, Substitution, …) owns its
 *     own block, so its Main.<team>.Score / Score_UD are dropped and every
 *     other field, identity included, is passed through untouched;
 *   - an action that owns Main (TEAM_x_SCORE_UPDATE, TEAM_NAMES_IN) is left
 *     alone — the scoreboard is theirs to set;
 *   - the full-graphic take (bare IN / OUT, group null) is left alone too: it
 *     legitimately carries the scoreline.
 *
 * A new overlay caption is covered the moment it appears in that map.
 *
 * Pure: returns the same object when nothing needs removing, and copies only
 * the branches it prunes.
 *
 * @param {Object} payload broker command: { renderer, layer, caption }
 * @returns {Object}
 */
export default function stripScoreFromOverlayTake(payload) {
  const group = resolveActionGroup(String(payload?.caption?.action || ""));
  if (!group || group === "Main") return payload;

  const layer = payload.layer;
  const gfx = payload?.caption?.data?.[layer];
  const main = gfx?.Main;
  if (!main || typeof main !== "object") return payload;

  let touched = false;
  const nextMain = { ...main };
  for (const team of Object.keys(nextMain)) {
    const block = nextMain[team];
    if (!block || typeof block !== "object") continue;
    if (!("Score" in block) && !("Score_UD" in block)) continue;
    const { Score, Score_UD, ...rest } = block;
    nextMain[team] = rest;
    touched = true;
  }
  if (!touched) return payload;

  return {
    ...payload,
    caption: {
      ...payload.caption,
      data: { ...payload.caption.data, [layer]: { ...gfx, Main: nextMain } },
    },
  };
}
